import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { isValidPhoneNumber } from "libphonenumber-js";
import { t } from "i18next";

import {
  CHECK_EXISTING_JOB_CANDIDATE,
  SUBMIT_JOB_CANDIDATE,
} from "../../../../utils/Controller/URLConstants";
import { allPhoneOptions } from "../../../../utils/Constants/dails";
import { validateEmail } from "../../../../utils/FieldValidation";
import {
  decodePhoneNumber,
  normalizePhoneNumber,
  parseFullName,
} from "../../../../utils/helperFunctions";
import { getOrCreateVisitorId } from "../../../../utils/security/requestProtection";
import { JobCandidateFormSection } from "./JobCandidateFormSection";
import ApplicationCompensationSummary from "./ApplicationCompensationSummary";

import { rememberOnboardingApplication } from "../../../../utils/applicationStorage";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneCode: "+46",
};

const EMPTY_ERRORS = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
};

function formValuesFromCandidate(data) {
  if (!data) return null;

  const candidate = data.candidate ?? {};
  const fullName = candidate.firstName
    ? `${candidate.firstName} ${candidate.lastName ?? ""}`.trim()
    : (data.fullName ?? "");
  const name = parseFullName(fullName);
  const decodedPhone = decodePhoneNumber(
    candidate.phoneNumber ?? data.phoneNumber ?? "",
  );

  return {
    firstName: name.firstName ?? "",
    lastName: name.lastName ?? "",
    email: candidate.email ?? data.email ?? "",
    phone: decodedPhone?.local ?? "",
    phoneCode: decodedPhone?.code ?? "+46",
  };
}

function formValuesFromProfile(profile) {
  if (!profile) return null;
  const decodedPhone = decodePhoneNumber(profile.phoneNumber ?? "");

  return {
    firstName: profile.firstName ?? "",
    lastName: profile.lastName ?? "",
    email: profile.email ?? "",
    phone: decodedPhone?.local ?? "",
    phoneCode: decodedPhone?.code ?? "+46",
  };
}

function mergeNonEmptyValues(fallback, preferred) {
  const values = { ...(fallback ?? {}) };
  Object.entries(preferred ?? {}).forEach(([key, value]) => {
    if (value !== "" && value != null) values[key] = value;
  });
  return values;
}

export function createInitialApplicationFormData({
  candidateData,
  applicationPrefill,
  authProfile,
}) {
  const values = candidateData
    ? formValuesFromCandidate(candidateData)
    : mergeNonEmptyValues(
        formValuesFromProfile(applicationPrefill),
        formValuesFromProfile(authProfile),
      );

  return { ...EMPTY_FORM, ...(values ?? {}) };
}

export function contactPersonFromCandidate(candidateData, fallback = null) {
  const savedContact = candidateData?.contactPerson;
  const userId =
    (typeof savedContact === "string" ? savedContact : savedContact?.uid) ??
    savedContact?.userId ??
    fallback?.userId;

  if (!userId) return fallback;

  return {
    userId,
    workplaceId:
      candidateData?.tenantId ??
      savedContact?.workplaceId ??
      fallback?.workplaceId ??
      "",
    firstName: savedContact?.firstName ?? fallback?.firstName ?? "",
    lastName: savedContact?.lastName ?? fallback?.lastName ?? "",
    profileImageUrl:
      savedContact?.profileImageUrl ?? fallback?.profileImageUrl ?? "",
    workplaceName:
      candidateData?.tenantName ?? fallback?.workplaceName ?? "",
    workplaceLogoUrl: fallback?.workplaceLogoUrl ?? "",
    email: savedContact?.email ?? fallback?.email ?? "",
  };
}

export function ApplicationContactForm({
  jobData,
  contactPerson,
  ensureContactPerson,
  candidateData,
  auth,
  applicationPrefill,
  showCompensation = false,
  compact = false,
  linkedInAccount,
  onUpdateCandidateData,
  onSubmitted,
  showToast,
}) {
  const [formData, setFormData] = useState(() =>
    createInitialApplicationFormData({
      candidateData,
      applicationPrefill,
      authProfile: auth?.profile,
    }),
  );
  const [formErrors, setFormErrors] = useState(EMPTY_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dirtyFields = useRef(new Set());

  useEffect(() => {
    const values = candidateData
      ? formValuesFromCandidate(candidateData)
      : mergeNonEmptyValues(
          formValuesFromProfile(applicationPrefill),
          formValuesFromProfile(auth?.profile),
        );
    if (!values) return;

    setFormData((current) => {
      const next = { ...current };
      Object.entries(values).forEach(([key, value]) => {
        if (!dirtyFields.current.has(key) && !current[key]) {
          next[key] = value;
        }
      });
      return next;
    });
  }, [candidateData, auth?.profile, applicationPrefill]);

  const updateFormData = (key, value) => {
    dirtyFields.current.add(key);
    setFormData((current) => ({ ...current, [key]: value }));
  };

  const validate = () => {
    const errors = { ...EMPTY_ERRORS };
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();

    if (!firstName) {
      errors.firstName = t("candidatedetail.form_error.firstname");
    }
    if (!lastName) {
      errors.lastName = t("candidatedetail.form_error.lastname");
    }
    if (!validateEmail(formData.email)) {
      errors.email = t("candidatedetail.form_error.invalid_email");
    }

    const fullPhoneNumber = normalizePhoneNumber(
      formData.phoneCode,
      formData.phone,
    );
    const country = allPhoneOptions().find(
      (item) => item.value === formData.phoneCode,
    );
    const validPhone = isValidPhoneNumber(fullPhoneNumber, country?.code, {
      defaultCallingCode: country?.value,
    });

    if (!formData.phone) {
      errors.phone = t("candidatedetail.form_error.phone");
    } else if (!validPhone) {
      errors.phone = t("candidatedetail.form_error.invalid_phone");
    }

    setFormErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const finish = ({ data, existed, resolvedContactPerson }) => {
    const authoritativeContactPerson = contactPersonFromCandidate(
      data,
      resolvedContactPerson ?? contactPerson,
    );
    rememberOnboardingApplication(jobData.jobId, data);
    onUpdateCandidateData?.(data);
    showToast?.(
      t(
        existed
          ? "candidatedetail.form.toast.already_candidate"
          : "candidatedetail.form.toast.interest_registered",
      ),
      { type: "success" },
    );
    onSubmitted?.({
      candidateData: data,
      existed,
      contactPerson: authoritativeContactPerson,
    });
  };

  const submit = async () => {
    if (isSubmitting || !validate()) return;

    const currentUser = auth?.currentUser;
    const email = formData.email.trim();
    const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
    const fullPhoneNumber = normalizePhoneNumber(
      formData.phoneCode,
      formData.phone,
    );

    setIsSubmitting(true);
    try {
      const visitorId = await getOrCreateVisitorId();
      const existingResponse = await axios.get(CHECK_EXISTING_JOB_CANDIDATE, {
        params: {
          job: jobData.jobId,
          email,
          visitorId: visitorId || undefined,
          ...(currentUser?.uid ? { uid: currentUser.uid } : {}),
          ...(currentUser?.email && currentUser.email !== email
            ? { authEmail: currentUser.email }
            : {}),
        },
      });

      if (existingResponse.data?.status === 0) {
        finish({
          data: existingResponse.data.jobCandidate,
          existed: true,
          resolvedContactPerson: contactPerson,
        });
        return;
      }

      const resolvedContactPerson = ensureContactPerson
        ? await ensureContactPerson()
        : contactPerson;
      const meta = jobData?.meta ?? {};
      const isProviderJob = Boolean(meta.bemloId || meta.medloId);

      if (
        !resolvedContactPerson?.userId ||
        (isProviderJob && !resolvedContactPerson?.workplaceId)
      ) {
        showToast?.(t("candidatedetail.form_error.no_contact_person"), {
          type: "error",
        });
        return;
      }

      const contactPersonId =
        resolvedContactPerson?.userId ?? jobData.contactPerson ?? "";
      const workplaceId = resolvedContactPerson?.workplaceId ?? "";
      const postData = {
        token: contactPersonId || "external",
        job: jobData.jobId,
        fullName,
        email,
        phone: fullPhoneNumber,
        reffererCanFollow: 0,
        cp: contactPersonId,
        workplaceId,
        visitorId: visitorId || undefined,
        ...(linkedInAccount ? { linkedInAccount } : {}),
      };

      const response = await axios.post(SUBMIT_JOB_CANDIDATE, postData);
      if (response.data?.status === 0) {
        finish({
          data: response.data.candidateData,
          existed: false,
          resolvedContactPerson,
        });
        return;
      }

      showToast?.(
        response.data?.error ||
          t("candidatedetail.form.toast.already_candidate"),
        { type: "info" },
      );
    } catch (error) {
      console.error("Error submitting job application contact details:", error);
      showToast?.(t("candidatedetail.form_error.invalid_information"), {
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showCompensation && <ApplicationCompensationSummary jobData={jobData} />}
      <JobCandidateFormSection
        formData={formData}
        updateFormData={updateFormData}
        onSend={submit}
        formErrors={formErrors}
        isApplyingCandidate={isSubmitting}
        candidateData={candidateData}
        auth={auth}
        compact={compact}
      />
    </>
  );
}

export default ApplicationContactForm;
